import { useState, useEffect, useRef, useCallback } from 'react';
import Peer from 'peerjs';

const LOBBY_STORAGE_KEY = 'dnd-master-lobby';
const PLAYER_LOBBY_STORAGE_KEY = 'dnd-player-lobby';

// Codice breve (4 caratteri) facile da dire e da leggere
const LOBBY_CODE_LENGTH = 4;
const LOBBY_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // senza I,O,L,1,0

const generateLobbyCode = () => {
  let code = '';
  for (let i = 0; i < LOBBY_CODE_LENGTH; i++) {
    code += LOBBY_CODE_CHARS.charAt(Math.floor(Math.random() * LOBBY_CODE_CHARS.length));
  }
  return code;
};

// Codifica masterId in base64url (senza +/=) per link/QR
export const encodeMasterId = (peerId) => {
  if (!peerId) return '';
  return btoa(peerId).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export const decodeMasterId = (encoded) => {
  if (!encoded) return '';
  try {
    const padding = '='.repeat((4 - (encoded.length % 4)) % 4);
    return atob(encoded.replace(/-/g, '+').replace(/_/g, '/') + padding);
  } catch (e) {
    return '';
  }
};

export const usePeer = () => {
  const [peer, setPeer] = useState(null);
  const [peerId, setPeerId] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');
  const peerRef = useRef(null);

  useEffect(() => {
    const initPeer = () => {
      try {
        // Use public PeerJS cloud server
        const newPeer = new Peer({
          debug: 1,
        });

        newPeer.on('open', (id) => {
          setPeerId(id);
          setIsConnected(true);
          console.log('Peer connected with ID:', id);
        });

        newPeer.on('error', (err) => {
          console.error('PeerJS error:', err);
          setError(err.message || 'Connection error');
          setIsConnected(false);
        });

        newPeer.on('disconnected', () => {
          setIsConnected(false);
          // Try to reconnect
          if (!newPeer.destroyed) {
            newPeer.reconnect();
          }
        });

        peerRef.current = newPeer;
        setPeer(newPeer);
      } catch (err) {
        console.error('Failed to initialize PeerJS:', err);
        setError('Failed to initialize connection');
      }
    };

    initPeer();

    return () => {
      if (peerRef.current && !peerRef.current.destroyed) {
        peerRef.current.destroy();
      }
    };
  }, []);

  const reconnect = useCallback(() => {
    if (peerRef.current && !peerRef.current.destroyed) {
      peerRef.current.reconnect();
    }
  }, []);

  return { peer, peerId, isConnected, error, reconnect };
};

// Master Lobby Hook - options: callbacks per applicare modifiche dai player e ribroadcast
export const useMasterLobby = (peer, peerId, options = {}) => {
  const {
    onUpdateCombatantFromPlayer,
    onModifyHpFromPlayer,
    onUpdateDeathSavesFromPlayer,
    onResetDeathSavesFromPlayer,
  } = options;
  const [lobbyCode, setLobbyCode] = useState('');
  const [players, setPlayers] = useState([]);
  const [combatState, setCombatState] = useState(null);
  const connectionsRef = useRef(new Map());

  // Load saved lobby on init
  useEffect(() => {
    const saved = localStorage.getItem(LOBBY_STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setLobbyCode(data.lobbyCode || '');
        setCombatState(data.combatState || null);
      } catch (e) {
        console.error('Error loading saved lobby:', e);
      }
    }
  }, []);

  // Save lobby state
  const saveLobbyState = useCallback((state) => {
    const data = {
      lobbyCode,
      combatState: state,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(LOBBY_STORAGE_KEY, JSON.stringify(data));
    setCombatState(state);
  }, [lobbyCode]);

  // Create new lobby
  const createLobby = useCallback(() => {
    const code = generateLobbyCode();
    setLobbyCode(code);
    setPlayers([]);
    setCombatState(null);
    localStorage.setItem(LOBBY_STORAGE_KEY, JSON.stringify({
      lobbyCode: code,
      combatState: null,
      savedAt: new Date().toISOString(),
    }));
    return code;
  }, []);

  // Resume existing lobby
  const resumeLobby = useCallback(() => {
    const saved = localStorage.getItem(LOBBY_STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      if (data.lobbyCode) {
        setLobbyCode(data.lobbyCode);
        setCombatState(data.combatState);
        return data.lobbyCode;
      }
    }
    return null;
  }, []);

  // Handle incoming connections
  useEffect(() => {
    if (!peer) return;

    const handleConnection = (conn) => {
      console.log('New player connecting:', conn.peer);

      conn.on('open', () => {
        connectionsRef.current.set(conn.peer, conn);
        
        // Send current state to new player
        if (combatState) {
          conn.send({
            type: 'state_sync',
            state: combatState,
            lobbyCode,
          });
        }
      });

      conn.on('data', (data) => {
        handlePlayerMessage(conn.peer, data);
      });

      conn.on('close', () => {
        connectionsRef.current.delete(conn.peer);
        setPlayers(prev => prev.filter(p => p.peerId !== conn.peer));
      });

      conn.on('error', (err) => {
        console.error('Connection error:', err);
      });
    };

    peer.on('connection', handleConnection);

    return () => {
      peer.off('connection', handleConnection);
    };
  }, [peer, combatState, lobbyCode]);

  // Handle messages from players
  const handlePlayerMessage = useCallback((senderPeerId, data) => {
    switch (data.type) {
      case 'join':
        setPlayers(prev => {
          if (prev.find(p => p.peerId === senderPeerId)) return prev;
          return [...prev, {
            peerId: senderPeerId,
            playerName: data.playerName,
            characters: data.characters || (data.character ? [data.character] : []), // Support both array and single
          }];
        });
        // Send confirmation
        const conn = connectionsRef.current.get(senderPeerId);
        if (conn) {
          conn.send({
            type: 'join_confirmed',
            lobbyCode,
            state: combatState,
          });
        }
        break;

      case 'update_character':
        setPlayers(prev => prev.map(p => 
          p.peerId === senderPeerId 
            ? { ...p, character: { ...p.character, ...data.updates } }
            : p
        ));
        break;

      case 'update_combatant':
        if (typeof onUpdateCombatantFromPlayer === 'function' && data.combatantId && data.updates) {
          onUpdateCombatantFromPlayer(data.combatantId, data.updates);
        }
        break;

      case 'modify_hp':
        if (typeof onModifyHpFromPlayer === 'function' && data.combatantId != null && typeof data.amount === 'number') {
          onModifyHpFromPlayer(data.combatantId, data.amount);
        }
        break;

      case 'update_death_saves':
        if (typeof onUpdateDeathSavesFromPlayer === 'function' && data.combatantId && (data.type === 'success' || data.type === 'failure')) {
          onUpdateDeathSavesFromPlayer(data.combatantId, data.type);
        }
        break;

      case 'reset_death_saves':
        if (typeof onResetDeathSavesFromPlayer === 'function' && data.combatantId) {
          onResetDeathSavesFromPlayer(data.combatantId);
        }
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  }, [lobbyCode, combatState, onUpdateCombatantFromPlayer, onModifyHpFromPlayer, onUpdateDeathSavesFromPlayer, onResetDeathSavesFromPlayer]);

  // Broadcast state to all players
  const broadcastState = useCallback((state) => {
    saveLobbyState(state);
    connectionsRef.current.forEach((conn) => {
      if (conn.open) {
        conn.send({
          type: 'state_sync',
          state,
        });
      }
    });
  }, [saveLobbyState]);

  // Send message to specific player
  const sendToPlayer = useCallback((peerId, message) => {
    const conn = connectionsRef.current.get(peerId);
    if (conn && conn.open) {
      conn.send(message);
    }
  }, []);

  // Close lobby
  const closeLobby = useCallback(() => {
    connectionsRef.current.forEach((conn) => {
      conn.send({ type: 'lobby_closed' });
      conn.close();
    });
    connectionsRef.current.clear();
    setPlayers([]);
    setLobbyCode('');
    setCombatState(null);
    localStorage.removeItem(LOBBY_STORAGE_KEY);
  }, []);

  // Codice completo: 4 lettere + id master in base64url (per link/QR; i player usano link o QR)
  const getFullLobbyCode = useCallback(() => {
    if (!lobbyCode || !peerId) return '';
    return `${lobbyCode}-${encodeMasterId(peerId)}`;
  }, [lobbyCode, peerId]);

  // Get shareable link (includes full lobby code)
  const getShareableLink = useCallback(() => {
    const baseUrl = window.location.origin;
    const fullCode = getFullLobbyCode();
    return `${baseUrl}?lobby=${fullCode}`;
  }, [getFullLobbyCode]);

  return {
    lobbyCode,
    players,
    combatState,
    createLobby,
    resumeLobby,
    broadcastState,
    sendToPlayer,
    closeLobby,
    getShareableLink,
    getFullLobbyCode,
    saveLobbyState,
    connections: connectionsRef.current,
  };
};

// Player Lobby Hook
export const usePlayerLobby = (peer, peerId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lobbyCode, setLobbyCode] = useState('');
  const [combatState, setCombatState] = useState(null);
  const [error, setError] = useState('');
  const connectionRef = useRef(null);

  // Connect to master
  const joinLobby = useCallback((code, masterId, playerName, characters) => {
    if (!peer) {
      setError('Peer not initialized');
      return;
    }

    setLobbyCode(code);
    setError('');

    const conn = peer.connect(masterId, { reliable: true });

    conn.on('open', () => {
      setIsConnected(true);
      connectionRef.current = conn;
      
      // Save lobby info to localStorage for reconnection
      localStorage.setItem(PLAYER_LOBBY_STORAGE_KEY, JSON.stringify({
        code,
        masterId,
        playerName,
        characters,
        savedAt: new Date().toISOString(),
      }));
      
      // Send join message with array of characters
      conn.send({
        type: 'join',
        playerName,
        characters: Array.isArray(characters) ? characters : (characters ? [characters] : []),
      });
    });

    conn.on('data', (data) => {
      handleMasterMessage(data);
    });

    conn.on('close', () => {
      setIsConnected(false);
      connectionRef.current = null;
    });

    conn.on('error', (err) => {
      setError(err.message || 'Connection failed');
      setIsConnected(false);
    });
  }, [peer]);

  // Handle messages from master
  const handleMasterMessage = useCallback((data) => {
    switch (data.type) {
      case 'state_sync':
        setCombatState(data.state);
        break;
      case 'join_confirmed':
        setLobbyCode(data.lobbyCode);
        setCombatState(data.state);
        break;
      case 'lobby_closed':
        setIsConnected(false);
        setCombatState(null);
        setError('Lobby closed by Master');
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }, []);

  // Send update to master (character sheet / profile)
  const sendUpdate = useCallback((updates) => {
    if (connectionRef.current && connectionRef.current.open) {
      connectionRef.current.send({
        type: 'update_character',
        updates,
      });
    }
  }, []);

  // Send combatant update to master (initiative, statuses, etc.) so master can apply and broadcast
  const sendCombatUpdate = useCallback((combatantId, updates) => {
    if (connectionRef.current && connectionRef.current.open && combatantId && updates && Object.keys(updates).length > 0) {
      connectionRef.current.send({
        type: 'update_combatant',
        combatantId,
        updates,
      });
    }
  }, []);

  // Send HP change so master applies (tempHp logic, etc.) and broadcasts
  const sendCombatModifyHp = useCallback((combatantId, amount) => {
    if (connectionRef.current && connectionRef.current.open && combatantId != null && typeof amount === 'number') {
      connectionRef.current.send({
        type: 'modify_hp',
        combatantId,
        amount,
      });
    }
  }, []);

  // Tiri salvezza: master applica e ribroadcast a tutti
  const sendDeathSavesUpdate = useCallback((combatantId, type) => {
    if (connectionRef.current && connectionRef.current.open && combatantId && (type === 'success' || type === 'failure')) {
      connectionRef.current.send({ type: 'update_death_saves', combatantId, type });
    }
  }, []);

  const sendDeathSavesReset = useCallback((combatantId) => {
    if (connectionRef.current && connectionRef.current.open && combatantId) {
      connectionRef.current.send({ type: 'reset_death_saves', combatantId });
    }
  }, []);

  // Leave lobby
  const leaveLobby = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
    setIsConnected(false);
    setLobbyCode('');
    setCombatState(null);
    localStorage.removeItem(PLAYER_LOBBY_STORAGE_KEY);
  }, []);

  // Get saved lobby info
  const getSavedLobby = useCallback(() => {
    const saved = localStorage.getItem(PLAYER_LOBBY_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved lobby:', e);
        return null;
      }
    }
    return null;
  }, []);

  return {
    isConnected,
    lobbyCode,
    combatState,
    error,
    joinLobby,
    sendUpdate,
    sendCombatUpdate,
    sendCombatModifyHp,
    sendDeathSavesUpdate,
    sendDeathSavesReset,
    leaveLobby,
    getSavedLobby,
  };
};
